/**
 * What crosses between the browser and the edge, defined once.
 *
 * This is the same seam as the JSON boundary in DESIGN §4 and the event schema in
 * §10, and it exists for the same reason those do: two declarations of one shape
 * drift, and the drift is silent. A response field renamed on the server does not
 * throw in the client — it reads `undefined` and the purchase screen spins forever.
 * So `apps/web` imports these types rather than restating them, and a rename is a
 * compile error on both sides at once.
 *
 * Types only, bar a few values (`MAX_NOTE_CHARS`, `TERMS_VERSION`, the two pages
 * Stripe sends a shopper back to and `CHECKOUT_CANCELED_HASH`, below) and one function
 * (`isCheckoutId`). It imports
 * nothing but the
 * license block, and that one deliberately: `License` is the composer's type, because
 * the composer is what stamps it on the page. The edge does not get to invent a shape
 * for a thing another package prints.
 */

import type { License } from "@planforge/pdf";

export type { License };

/**
 * Where Stripe sends a shopper who backs out of checkout: the configurator, marked so
 * it can say what happened.
 *
 * A value for the reason the other two are: the edge writes it into Stripe's
 * `cancel_url` and the configurator reads it back, and two copies of one address drift
 * into a cancel that lands on the right screen and says nothing. Nothing is in it but
 * the fact of the cancel — Stripe fills in the session id only on the way to the
 * success URL, so the cancel return cannot say which checkout it was. It does not need
 * to: the design was kept in this browser before the redirect (`state/saved.ts` in
 * `apps/web`), so the shopper comes back to their own shed either way.
 */
export const CHECKOUT_CANCELED_HASH = "#/?checkout=canceled";

/**
 * The page a payment comes back to, and the list of what a browser has bought: the
 * plans page, whatever plan type was sold. The edge writes it into Stripe's
 * `success_url` and the app serves it, so it is one value for both.
 */
export const PLANS_PATH = "/plans/";

/**
 * The shed configurator's page, which a canceled checkout goes back to with
 * `CHECKOUT_CANCELED_HASH`. Sheds are the only plan type on sale; a second type that
 * sells will name its own configurator in the checkout request, and this is where that
 * address is defined.
 */
export const SHED_DESIGNER_PATH = "/sheds/design/";

/** A Checkout session id, and nothing that could be anything else in a URL path. */
const CHECKOUT_ID = /^cs_(test|live)_[0-9A-Za-z]{1,240}$/;

/**
 * Is this the shape of a Stripe Checkout session id?
 *
 * A function here, rather than a regex in each package, for the reason the values
 * are: the edge refuses an id that fails it before Stripe is asked, and the return
 * screen refuses the same id before it says anything about a payment. Two copies drift
 * into a link the screen treats as an order and the edge calls garbage — and the
 * screen is the one that tells a stranger with a made-up link "your payment is fine".
 *
 * A shape is all it checks. `cs_test_abc` passes and was never paid for, which is why
 * the screens that take one still say "if you paid" rather than "you paid".
 */
export function isCheckoutId(value: unknown): value is string {
  return typeof value === "string" && CHECKOUT_ID.test(value);
}

/**
 * What `create-checkout` answers with: where to send the shopper, and the id of the
 * Checkout session that will be their purchase.
 *
 * The buy button posts nothing — the design never leaves the browser — so there is no
 * request type. `checkoutId` is Stripe's own session id: the browser files its pending
 * purchase under it before the redirect, and Stripe hands the same id back in the
 * success URL, which is how the two halves of a purchase find each other with nothing
 * stored on our side.
 */
export interface CheckoutStartResponse {
  checkoutId: string;
  /** Stripe's hosted Checkout. The browser navigates here. */
  url: string;
}

/**
 * The answer to "has this checkout been paid", which the edge asks Stripe.
 *
 * `pending` is not an error and is not a 404. The browser can ask while a payment is
 * still confirming, and a 404 would tell a poller to stop at the one moment this
 * endpoint exists for. A session Stripe has never heard of answers the same way an
 * unpaid one does.
 */
export type PurchaseResponse =
  | { status: "pending" }
  | {
      status: "ready";
      license: License;
      amountCents: number;
      currency: string;
    };

/**
 * Where a report was sent from.
 *
 * `crash` is the error boundary's fallback screen; `configurator` is the button in the
 * panel. The difference is worth a field because the two mean different things about
 * the spec attached: from the panel it is the design the customer is looking at and
 * thinks is wrong, from a crash it is the design that was on screen when the app fell
 * over — which may be the bug, or may be an innocent bystander.
 */
export type ReportContext = "configurator" | "crash";

/**
 * As long as a report's note may be: a screenful of prose.
 *
 * The one value in this module, and here for the reason the types are — the form's
 * `maxLength` and the edge's refusal are one agreement, and two copies of a limit
 * drift into a form that lets a customer type a note the edge then turns away. It
 * costs the bundle one number.
 */
export const MAX_NOTE_CHARS = 4000;

/**
 * The terms of sale the site is showing, named by the day they took effect.
 *
 * Here for the same reason as `MAX_NOTE_CHARS`: the buy button asks the shopper to
 * accept the terms at this version, and `create-checkout` stamps the same version on
 * the Stripe session — so Stripe's record of a sale says which terms it was sold
 * under, and we still keep nothing (DESIGN §9). The site and the edge deploy
 * together, so one constant is one agreement. Change it in the same commit as any
 * change to what the terms say.
 */
export const TERMS_VERSION = "2026-10-16";

/**
 * What the report form posts.
 *
 * The spec rides whole, every time. That is the whole reason this replaced a `mailto:`,
 * whose body had no length a client could be trusted with — so the reports that
 * arrived without their design were systematically the large ones, the sheds with the
 * most places for a framing rule to be wrong (MILESTONES, M6.5).
 *
 * The three versions are what make the spec reproducible rather than merely present:
 * the engine that built what the customer saw, the spec version this client writes,
 * and the build of the app around both. There is deliberately no field for an address.
 * We do not reply to reports, so nothing here needs to know who sent one (DESIGN §9,
 * "email enters once").
 */
export interface ReportRequest {
  /** `unknown` because the edge must not trust it. */
  spec: unknown;
  /** What looks wrong, in the customer's words. May be empty: the spec is the report. */
  note: string;
  context: ReportContext;
  /** The engine semver the client loaded, or `"not-loaded"` if it never did. */
  engineVersion: string;
  /** The `kSpecVersion` this client writes new specs at. */
  specVersion: number;
  /** The app build: its package version and the commit it was built from. */
  buildId: string;
}

/**
 * The receipt.
 *
 * `reference` names the report and grants nothing — there is no endpoint that reads a
 * report back, so it is something to quote to us and not a key to anything.
 * `addressesRemoved` is how many email addresses the edge took out of the note before
 * storing it, so the form can say so rather than leave the customer expecting a reply.
 */
export interface ReportResponse {
  reference: string;
  addressesRemoved: number;
}

/** A refusal. `code` is the stable half; `message` is prose and may be reworded. */
export interface ApiError {
  error: { code: string; message: string };
}
